import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import { voltAiBot } from "@/assets/images/chat";
import { externalUrls } from "@/constants/urls";
import { routes } from "@/routes/routes";
import { trackingService, useTrackOnboardingCompleted } from "@/lib/tracking";
import { ContactSupportTeamButton } from "@/features/support";
import { useLoanOffer, useOfferRequest } from "../../hooks";
import { OfferStatus } from "../../types/offer";
import { OtherIncomeBanner } from "../../components/OtherIncomeBanner";
import styles from "./NoOfferAvailablePage.module.scss";

/**
 * No final offer after the ANAF check: the income to confirm, or nothing for
 * now. A Volt product offered instead has its own screen.
 */
export function NoOfferAvailablePage() {
  const navigate = useNavigate();
  const { proofOfIncomeRequired, isLoading: isLoanOfferLoading } =
    useLoanOffer(true);
  const { offer, isLoading: isOfferLoading } = useOfferRequest(true);

  const isLoading = isLoanOfferLoading || isOfferLoading;
  // As the chat sees it, so the two agree on when the offers expired.
  const isRequestExpired =
    !!offer?.isExpired &&
    (offer.status === OfferStatus.Complete ||
      offer.status === OfferStatus.NoOfferAvailable);
  const variant = proofOfIncomeRequired ? "income" : "none";
  const isShown = !isLoading && !isRequestExpired;

  // Skipped while loading or redirecting, so it fires on the screen shown.
  useTrackOnboardingCompleted(isShown);

  useEffect(() => {
    if (isShown) {
      void trackingService.trackEvent("final_no_offer_shown", { variant });
    }
  }, [isShown, variant]);

  if (isLoading) {
    return <PageLoader />;
  }

  // The chat asks for the consents again and requests a new offer.
  if (isRequestExpired) {
    return <Navigate to={routes.wizard.base} replace />;
  }

  if (variant === "income") {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.title}>Veniturile nu coincid 💰</h1>
          <p className={styles.description}>
            Venitul identificat la ANAF diferă de cel introdus de tine.
          </p>
        </div>
        <OtherIncomeBanner
          trackingId="final_no_offer_proof_of_income"
          onClick={() => navigate(routes.offer.proofOfIncome)}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Nu am găsit o ofertă potrivită acum 😔</h1>
        <p className={styles.description}>
          Momentan nu întrunești condițiile de eligibilitate pentru un împrumut.
        </p>
      </div>

      <section className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.bot}>
            <img src={voltAiBot} alt="" className={styles.botAvatar} />
            <div className={styles.botName}>
              <p className={styles.botTitle}>Volt AI</p>
              <p className={styles.botStatus}>online</p>
            </div>
          </div>
          <div className={styles.bubbles}>
            <p className={styles.bubble}>Poți încerca din nou peste 30 de zile.</p>
            <p className={styles.bubble}>
              📱 În Volt îți arăt ce poți face pentru a avea acces la oferte mai
              bune.
            </p>
          </div>
        </div>
        <div className={styles.cardActions}>
          <Button
            trackingId="final_no_offer_download_volt"
            text="Descarcă aplicația Volt"
            rightIcon={<Icon name="arrow-right" />}
            onClick={() => {
              window.location.href = externalUrls.volt.appDownload;
            }}
            className={styles.cardButton}
          />
          <ContactSupportTeamButton />
        </div>
      </section>
    </div>
  );
}
